import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Camera } from 'lucide-react';
import { getCategorias } from '../api/api';
import { useToast } from '../Toast';
import { useTheme } from '../context/ThemeContext';
import BarcodeScanner from './BarcodeScanner';


const ProductoForm = ({ producto, onClose, onSubmit }) => {
  const toast = useToast();
  const { theme } = useTheme();
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: producto?.nombre || '',
    descripcion: producto?.descripcion || '',
    precio_costo: producto?.precio_costo || '',
    margen_porcentaje: producto?.margen_porcentaje || 25, 
    stock: producto?.stock || '',
    stock_minimo: producto?.stock_minimo || 10,
    categoria: producto?.categoria || '',
    codigo_barras: producto?.codigo_barras || ''
  });

  // Estados para categorías
  const [categoriaInput, setCategoriaInput] = useState(producto?.categoria || '');
  const [categorias, setCategorias] = useState([]);
  const [categoriasCache, setCategoriasCache] = useState([]);
  const [sugerenciasLocales, setSugerenciasLocales] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  
  // Estados de paginación para categorías
  const [skipCategorias, setSkipCategorias] = useState(0);
  const [hasMoreCategorias, setHasMoreCategorias] = useState(true);
  const [totalCategorias, setTotalCategorias] = useState(0);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const LIMIT_CATEGORIAS = 20;
  
  // Referencias
  const inputCategoriaRef = useRef(null);
  const sugerenciasRef = useRef(null);
  const contenedorScrollRef = useRef(null);
  const observerCategoriasRef = useRef(null);
  const timerBusquedaRef = useRef(null);
  const scrollAjustadoRef = useRef(false);

  // Scanner con celular
  const [showScanner, setShowScanner] = useState(false);

  // CALCULAR PRECIO DE VENTA AUTOMÁTICAMENTE
  const precioVentaCalculado = formData.precio_costo && formData.margen_porcentaje
    ? (parseFloat(formData.precio_costo) * (1 + parseFloat(formData.margen_porcentaje) / 100)).toFixed(2)
    : 0;

  // CALCULAR GANANCIA POR UNIDAD
  const gananciaPorUnidad = formData.precio_costo
    ? (precioVentaCalculado - formData.precio_costo).toFixed(2)
    : 0;

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (timerBusquedaRef.current) {
        clearTimeout(timerBusquedaRef.current);
      }
    };
  }, []);

  // Ajustar scroll del modal cuando aparecen sugerencias
  const ajustarScrollModal = useCallback(() => {
    if (!inputCategoriaRef.current || !contenedorScrollRef.current) return;
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!sugerenciasRef.current) return;
        
        const inputRect = inputCategoriaRef.current.getBoundingClientRect();
        const contenedorRect = contenedorScrollRef.current.getBoundingClientRect();
        const sugerenciasRect = sugerenciasRef.current.getBoundingClientRect();
        
        const espacioInferior = contenedorRect.bottom - inputRect.bottom;
        const alturaDropdown = Math.min(sugerenciasRect.height, 200);
        
        if (espacioInferior < alturaDropdown + 20) {
          const scrollAmount = inputRect.top - contenedorRect.top + 
                              contenedorScrollRef.current.scrollTop - 20;
          
          contenedorScrollRef.current.scrollTo({
            top: scrollAmount,
            behavior: 'smooth'
          });
        }
      }, 100);
    });
  }, []);

  useEffect(() => {
    if (mostrarSugerencias && sugerenciasLocales.length > 0 && !scrollAjustadoRef.current) {
      ajustarScrollModal();
      scrollAjustadoRef.current = true;
    }
  }, [mostrarSugerencias, sugerenciasLocales, ajustarScrollModal]);

  // Cargar cache inicial de categorías
  useEffect(() => {
    const cargarCategoriasIniciales = async () => {
      try {
        const response = await getCategorias({ skip: 0, limit: 100 });
        
        let categoriasIniciales = [];
        if (Array.isArray(response.data)) {
          categoriasIniciales = response.data;
        } else if (response.data && response.data.categorias) {
          categoriasIniciales = response.data.categorias;
        }
        
        setCategoriasCache(categoriasIniciales);
      } catch (error) {
        console.error('Error cargando categorías iniciales:', error);
      }
    };
    
    cargarCategoriasIniciales();
  }, []);

  // Cargar categorías con paginación
  const cargarCategorias = async (skipValue = 0, reset = false) => {
    if (!hasMoreCategorias && !reset) return;
    if (loadingCategorias) return;
    
    try {
      setLoadingCategorias(true);
      
      const params = {
        skip: skipValue,
        limit: LIMIT_CATEGORIAS,
        ...(categoriaInput && { busqueda: categoriaInput })
      };

      const response = await getCategorias(params);
      
      let nuevasCategorias, total, has_more;
      
      if (Array.isArray(response.data)) {
        nuevasCategorias = response.data;
        total = response.data.length;
        has_more = false;
      } else if (response.data && response.data.categorias) {
        nuevasCategorias = response.data.categorias;
        total = response.data.total;
        has_more = response.data.has_more;
      } else {
        console.error('Formato de respuesta inesperado:', response.data);
        nuevasCategorias = [];
        total = 0;
        has_more = false;
      }

      if (reset) {
        setCategorias(nuevasCategorias);
        if (categoriaInput && nuevasCategorias.length > 0) {
          setSugerenciasLocales(prev => {
            const combined = [...prev, ...nuevasCategorias];
            return [...new Set(combined)];
          });
        }
        
        if (!categoriaInput) {
          setCategoriasCache(prev => {
            const merged = [...prev, ...nuevasCategorias];
            return [...new Set(merged)];
          });
        }
      } else {
        setCategorias(prev => [...prev, ...nuevasCategorias]);
        if (categoriaInput) {
          setSugerenciasLocales(prev => {
            const combined = [...prev, ...nuevasCategorias];
            return [...new Set(combined)];
          });
        }
      }
      
      setTotalCategorias(total);
      setHasMoreCategorias(has_more);
      setSkipCategorias(skipValue + LIMIT_CATEGORIAS);

    } catch (error) {
      console.error('Error cargando categorías:', error);
    } finally {
      setLoadingCategorias(false);
    }
  };

  // Intersection Observer para scroll infinito
  const lastCategoriaRef = useCallback(node => {
    if (loadingCategorias) return;
    if (observerCategoriasRef.current) observerCategoriasRef.current.disconnect();
    
    observerCategoriasRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreCategorias) {
        cargarCategorias(skipCategorias);
      }
    });
    
    if (node) observerCategoriasRef.current.observe(node);
  }, [loadingCategorias, hasMoreCategorias, skipCategorias]);

  // Manejar cambio en input de categoría
  const handleCategoriaChange = (e) => {
    const valor = e.target.value;
    setCategoriaInput(valor);
    setFormData(prev => ({ ...prev, categoria: valor }));
    
    if (timerBusquedaRef.current) {
      clearTimeout(timerBusquedaRef.current);
    }
    
    if (valor.trim() === '') {
      setSugerenciasLocales([]);
      setMostrarSugerencias(false);
      scrollAjustadoRef.current = false;
      return;
    }
    
    const valorLower = valor.toLowerCase();
    const sugerenciasFiltradas = categoriasCache.filter(cat =>
      cat.toLowerCase().includes(valorLower)
    );
    
    setSugerenciasLocales(sugerenciasFiltradas);
    setMostrarSugerencias(true);
    scrollAjustadoRef.current = false;
  };

  const seleccionarCategoria = (categoria) => {
    setCategoriaInput(categoria);
    setFormData(prev => ({ ...prev, categoria }));
    setMostrarSugerencias(false);
    scrollAjustadoRef.current = false;
  };

  const crearNuevaCategoria = () => {
    setFormData(prev => ({ ...prev, categoria: categoriaInput }));
    setMostrarSugerencias(false);
    scrollAjustadoRef.current = false;
  };

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sugerenciasRef.current &&
        !sugerenciasRef.current.contains(event.target) &&
        inputCategoriaRef.current &&
        !inputCategoriaRef.current.contains(event.target)
      ) {
        setMostrarSugerencias(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Manejar código escaneado
  const handleScanSuccess = (barcode) => {
    setFormData(prev => ({ ...prev, codigo_barras: barcode }));
    setShowScanner(false);
    toast.success(`Código escaneado: ${barcode}`);
  };

  // Validar y enviar formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.categoria || formData.categoria.trim() === '') {
      toast.warning('La categoría es obligatoria');
      return;
    }
    
    if (parseFloat(formData.margen_porcentaje) < 0) {
      toast.warning('El margen debe ser mayor o igual a 0%');
      return;
    }
    
    // Enviar sin precio_venta, se calculará en backend
    onSubmit(formData);
  };

  // Actualizar campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'precio_costo' || name === 'margen_porcentaje' || name === 'stock' || name === 'stock_minimo' 
        ? parseFloat(value) || 0 
        : value
    }));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.bg.card,
        borderRadius: '0.5rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadow.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '2rem 2rem 0 2rem',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: theme.text.primary }}>
              {producto ? 'Editar Producto' : 'Nuevo Producto'}
            </h3>
            <button 
              onClick={onClose} 
              style={{ 
                cursor: 'pointer', 
                border: 'none', 
                background: 'none',
                color: theme.text.secondary,
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '0.25rem',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.bg.hover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}>
          {/* Contenido scrolleable */}
          <div 
            ref={contenedorScrollRef}
            style={{
              padding: '0 2rem',
              overflowY: 'auto',
              flex: 1
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Nombre */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                  Nombre <span style={{ color: theme.brand.danger }}>*</span>
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="input"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: theme.input.bg,
                    border: `1px solid ${theme.input.border}`,
                    borderRadius: '0.375rem',
                    color: theme.text.primary,
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Descripción */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                  Descripción
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  className="input"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: theme.input.bg,
                    border: `1px solid ${theme.input.border}`,
                    borderRadius: '0.375rem',
                    color: theme.text.primary,
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* NUEVO: Precio de Costo y Margen de Ganancia */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                    Precio de Costo <span style={{ color: theme.brand.danger }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="precio_costo"
                    value={formData.precio_costo}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    className="input"
                    placeholder="100.00"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: theme.input.bg,
                      border: `1px solid ${theme.input.border}`,
                      borderRadius: '0.375rem',
                      color: theme.text.primary,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                    Margen de Ganancia (%) <span style={{ color: theme.brand.danger }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="margen_porcentaje"
                    value={formData.margen_porcentaje}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    className="input"
                    placeholder="25"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: theme.input.bg,
                      border: `1px solid ${theme.input.border}`,
                      borderRadius: '0.375rem',
                      color: theme.text.primary,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* MOSTRAR PRECIO DE VENTA CALCULADO */}
              {formData.precio_costo > 0 && formData.margen_porcentaje >= 0 && (
                <div style={{
                  backgroundColor: theme.bg.active,
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `2px solid ${theme.brand.primary}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.brand.primary }}>
                      Precio de Venta (calculado):
                    </p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: theme.brand.primary }}>
                      ${precioVentaCalculado}
                    </p>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: theme.text.secondary }}>
                    <span>Ganancia por unidad:</span>
                    <span style={{ fontWeight: 600, color: theme.brand.primary }}>${gananciaPorUnidad}</span>
                  </div>
                </div>
              )}

              {/* Stock */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                    Stock <span style={{ color: theme.brand.danger }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    required
                    min="0"
                    className="input"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: theme.input.bg,
                      border: `1px solid ${theme.input.border}`,
                      borderRadius: '0.375rem',
                      color: theme.text.primary,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                    Stock Mínimo
                  </label>
                  <input
                    type="number"
                    name="stock_minimo"
                    value={formData.stock_minimo}
                    onChange={handleChange}
                    min="0"
                    className="input"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: theme.input.bg,
                      border: `1px solid ${theme.input.border}`,
                      borderRadius: '0.375rem',
                      color: theme.text.primary,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* Categoría y código de barras */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {/* Categoría con autocompletado */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
                    Categoría <span style={{ color: theme.brand.danger }}>*</span>
                  </label>
                  <input
                    ref={inputCategoriaRef}
                    type="text"
                    value={categoriaInput}
                    onChange={handleCategoriaChange}
                    onFocus={() => {
                      const valor = categoriaInput.trim();
                      if (valor !== '') {
                        const valorLower = valor.toLowerCase();
                        const sugerenciasFiltradas = categoriasCache.filter(cat =>
                          cat.toLowerCase().includes(valorLower)
                        );
                        setSugerenciasLocales(sugerenciasFiltradas);
                        setMostrarSugerencias(true);
                        scrollAjustadoRef.current = false;
                        
                        if (sugerenciasFiltradas.length < 5 && categorias.length === 0) {
                          cargarCategorias(0, true);
                        }
                      }
                    }}
                    required
                    className="input"
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: theme.input.bg,
                      border: `1px solid ${theme.input.border}`,
                      borderRadius: '0.375rem',
                      color: theme.text.primary,
                      fontSize: '0.875rem'
                    }}
                  />
                  
                  {/* Dropdown de sugerencias */}
                  {mostrarSugerencias && (
                    <div
                      ref={sugerenciasRef}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: theme.bg.card,
                        border: `1px solid ${theme.border.light}`,
                        borderRadius: '0.375rem',
                        marginTop: '0.25rem',
                        maxHeight: '100px',
                        overflowY: 'auto',
                        boxShadow: theme.shadow.md,
                        zIndex: 1000
                      }}
                    >
                      {sugerenciasLocales.length > 0 ? (
                        <>
                          {sugerenciasLocales.map((cat, idx) => {
                            const isLast = idx === sugerenciasLocales.length - 1;
                            return (
                              <div
                                key={idx}
                                ref={isLast ? lastCategoriaRef : null}
                                onClick={() => seleccionarCategoria(cat)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer',
                                  borderBottom: idx < sugerenciasLocales.length - 1 ? `1px solid ${theme.border.light}` : 'none',
                                  transition: 'background-color 0.15s',
                                  fontSize: '0.875rem',
                                  color: theme.text.primary
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.bg.hover}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.bg.card}
                              >
                                {cat}
                              </div>
                            );
                          })}
                          
                          {loadingCategorias && (
                            <div style={{ 
                              padding: '0.5rem 0.75rem', 
                              textAlign: 'center',
                              color: theme.text.secondary,
                              fontSize: '0.8125rem'
                            }}>
                              Buscando más categorías...
                            </div>
                          )}
                          
                          <div
                            onClick={crearNuevaCategoria}
                            style={{
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              color: theme.brand.primary,
                              fontWeight: 600,
                              transition: 'background-color 0.15s',
                              borderTop: `2px solid ${theme.border.light}`,
                              fontSize: '0.875rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.bg.active}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.bg.card}
                          >
                            + Agregar "{categoriaInput}"
                          </div>
                        </>
                      ) : loadingCategorias ? (
                        <div style={{ 
                          padding: '0.75rem 1rem', 
                          color: theme.text.secondary,
                          textAlign: 'center',
                          fontSize: '0.875rem'
                        }}>
                          Buscando categorías...
                        </div>
                      ) : (
                        <div
                          onClick={crearNuevaCategoria}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            color: theme.brand.primary,
                            fontWeight: 600,
                            transition: 'background-color 0.15s',
                            fontSize: '0.875rem'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.bg.active}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.bg.card}
                        >
                          + Agregar "{categoriaInput}"
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Código de barras con botón de cámara */}
<div>
  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: theme.text.primary }}>
    Código de Barras <span style={{ color: theme.brand.danger }}>*</span>
  </label>
  <div style={{ display: 'flex', gap: '0.5rem' }}>
    <input
      type="text"
      name="codigo_barras"
      value={formData.codigo_barras}
      onChange={handleChange}
      required
      className="input"
      placeholder="7891234567890"
      style={{
        flex: 1,
        padding: '0.5rem',
        backgroundColor: theme.input.bg,
        border: `1px solid ${theme.input.border}`,
        borderRadius: '0.375rem',
        color: theme.text.primary,
        fontSize: '0.875rem'
      }}
    />
    <button
      type="button"
      onClick={() => setShowScanner(true)}
      style={{
        padding: '0.5rem 0.75rem',
        backgroundColor: theme.brand.primary,
        color: theme.text.white,
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.2s',
        flexShrink: 0
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
      title="Escanear código de barras"
    >
      <Camera size={20} />
    </button>
  </div>
</div>
              </div>
            </div>
          </div>

          {/* Footer fijo con botones */}
          <div style={{
            padding: '1rem 2rem 2rem 2rem',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button 
                type="submit" 
                className="btn btn-success" 
                style={{ 
                  flex: 1,
                  padding: '0.625rem 1.25rem',
                  backgroundColor: theme.brand.success,
                  color: theme.text.white,
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                {producto ? 'Actualizar' : 'Crear'}
              </button>
              <button 
                type="button" 
                onClick={onClose} 
                className="btn" 
                style={{ 
                  flex: 1,
                  padding: '0.625rem 1.25rem',
                  backgroundColor: theme.text.secondary,
                  color: theme.text.white,
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>
      </div>
      {/* Scanner de códigos de barras */}
      {showScanner && (
        <BarcodeScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default ProductoForm;